/**
 * Fingerprint - Fingerprint capture functionality using Java applet.
 * Handles fingerprint image/template capture and field management.
 */

// External dependencies


/** Element object interface */
interface ElementObject {
	formObj: FormObject;
	get(): string;
	set(value: string): void;
}

/** Form object interface */
interface FormObject {
	name: string;
	getElement(name: string): ElementObject;
}

/** Deploy Java interface */
interface DeployJava {
	runApplet(attributes: AppletAttributes, params: AppletParams, version: string): void;
}

/** Applet attributes */
interface AppletAttributes {
	id: string;
	codebase: string;
	code: string;
	width: number;
	height: number;
	MAYSCRIPT: boolean;
}

/** Applet parameters */
interface AppletParams {
	jnlp_href: string;
}

/** Finger object data from applet */
interface FingerObject {
	imageData: string;
	templateData: string;
}

/** Fingerprint applet interface */
interface FingerPrintApplet {
	getTemplateQuality(): number;
	setImageEventEnabled(enabled: boolean): void;
	setTemplateEventEnabled(enabled: boolean): void;
	setImageTypeAsString(type: string): void;
	setImageQuality(quality: number): void;
	setDisplayModeAsString(mode: string): void;
}

/** Fingerprint object instance interface */
interface FingerPrintObjInstance {
	applet: FingerPrintApplet | null;
	imageData: string;
	templateData: string;
	width: number;
	height: number;
	quality: number | null;
	notifyOnImageFn: (obj: FingerPrintObjInstance) => void;
	notifyOnTemplateFn: (obj: FingerPrintObjInstance) => void;
	notifyOnImage(functionRef: (obj: FingerPrintObjInstance) => void): void;
	notifyOnTemplate(functionRef: (obj: FingerPrintObjInstance) => void): void;
	setImageData(newVal: string): void;
	setTemplateData(newVal: string): void;
	setImageEventEnabled(newVal?: boolean): void;
	setTemplateEventEnabled(newVal?: boolean): void;
	setImageType(newVal: string): void;
	setImageQuality(newVal: number): void;
	setDisplayMode(newVal: string): void;
}

/** Fingerprint field instance interface */
interface FingerPrintFieldInstance {
	prefix: string;
	image: string;
	template: string;
	form: string;
	view: HTMLElement;
	time: string | undefined;
	activateHandler: () => void;
	clearHandler: () => void;
	activate(): void;
	clear(): void;
	show(): void;
	setImage(fingerObj: FingerObject): void;
	setTemplate(fingerObj: FingerObject): void;
	set(fingerObj: FingerObject): void;
}

/** Fingerprint module interface */
interface FingerPrintModule {
	fields: { [key: string]: FingerPrintFieldInstance };
	curField: FingerPrintFieldInstance | null;
	register(fieldPrefix: string, formName?: string | null, timeStamp?: string): void;
	clear(prefix: string): void;
	activate(prefix: string): void;
	sendImageToField(fieldName: string, formName?: string | null): void;
	sendTemplateToField(fieldName: string, formName?: string | null): void;
	image64(imageData: string): void;
	template64(templateData: string): void;
	writeApplet(displayMode?: string | null, version?: number, width?: number, height?: number): void;
}

var fingerPrintObj: FingerPrintObjInstance | null = null;

var fingerPrint: FingerPrintModule = {
	fields: {},
	curField: null,
	register: function (fieldPrefix: string, formName?: string | null, timeStamp?: string): void {
		var imageField: string = fieldPrefix + ".imageBase64";
		var templateField: string = fieldPrefix + ".templateBase64";
		var viewPosition: HTMLElement = document.getElementById(fieldPrefix + ".readOnly")!;
		viewPosition.style.textAlign = "center";
		viewPosition.style.verticalAlign = "middle";
		if (formName == null || formName == '') formName = getElementObj(imageField).formObj.name;
		var fingerprintDiv = document.getElementById("fingerprintDiv");
		if (fingerprintDiv) fingerprintDiv.style.position = "inherit";

		var activateHandler = function (): void { fingerPrint.activate(fieldPrefix); };
		var clearHandler = function (): void { fingerPrint.clear(fieldPrefix); };

		this.fields[fieldPrefix] = {
			prefix: fieldPrefix,
			image: imageField,
			template: templateField,
			form: formName!,
			view: viewPosition,
			time: timeStamp,
			activateHandler: activateHandler,
			clearHandler: clearHandler,
			activate: activateHandler,
			clear: clearHandler,
			show: function (): void {
				var imgContent: string = getFormObj(this.form).getElement(this.image).get();
				var fpDiv = document.getElementById("fingerprintDiv");
				if (fpDiv) {
					fpDiv.style.display = 'none';
					document.body.appendChild(fpDiv);
				}
				var closestTable = this.view.closest("table") as HTMLElement | null;
				if (closestTable) closestTable.style.border = "1px solid black";

				// Remove old event listeners
				this.view.removeEventListener("click", this.activateHandler);
				this.view.querySelectorAll("img").forEach((img: Element) => {
					img.removeEventListener("click", this.activateHandler);
					img.removeEventListener("click", this.clearHandler);
				});

				if (imgContent.length > 20) {
					var date: string = this.time ? this.time + " &nbsp; " : "";
					this.view.innerHTML = '<img src="data:image/jpg;base64,' + imgContent + '" alt="Fingerprint" style="display:none"><br>'
						+ date + ' <img src="/images/buttons/btn_clear.gif" alt="Clear" style="width:47px;height:16">';
					var fprint = this.view.querySelector("img") as HTMLImageElement;
					var t = this;
					fprint.addEventListener("click", this.activateHandler);
					fprint.addEventListener("load", function (): void {
						var w: number = fprint.width, h: number = fprint.height;
						var maxH: number = fingerPrintObj!.height - 22.0, maxW: number = fingerPrintObj!.width - 6.0;
						var vrt: number = h / maxH, hz: number = w / maxW, scale: number = Math.max(vrt, hz);
						w = parseInt(String(w / scale)); h = parseInt(String(h / scale));
						fprint.width = w;
						fprint.height = h;
						fprint.style.display = '';
					});
					var clearImg = this.view.querySelectorAll("img")[1] as HTMLImageElement;
					if (clearImg) clearImg.addEventListener("click", this.clearHandler);
				} else {
					this.view.addEventListener("click", this.activateHandler);
					this.view.innerHTML = "Click to record fingerprint";
				}
			},
			setImage: function (fingerObj: FingerObject): void {
				var t = this;
				getFormObj(t.form).getElement(this.image).set(fingerObj.imageData);
				t.time = "";
				setTimeout(function (): void { t.show(); }, 100);
			},
			setTemplate: function (fingerObj: FingerObject): void {
				getFormObj(this.form).getElement(this.template).set(fingerObj.templateData);
			},
			set: function (fingerObj: FingerObject): void {
				this.setImage(fingerObj);
				this.setTemplate(fingerObj);
			}
		};
		this.fields[fieldPrefix].show();
	},
	clear: function (prefix: string): void {
		this.fields[prefix].set({ imageData: "", templateData: "" });
	},
	activate: function (prefix: string): void {
		var field: FingerPrintFieldInstance = this.fields[prefix];
		if (this.curField && this.curField != field) {
			this.curField.show();
		}
		this.curField = field;
		field.view.innerHTML = "";
		var fpDiv = document.getElementById("fingerprintDiv");
		if (fpDiv) {
			field.view.appendChild(fpDiv);
			fpDiv.style.display = '';
			var closestTable = fpDiv.closest("table") as HTMLElement | null;
			if (closestTable) closestTable.style.border = "0px";
		}
		var fingerApplet = document.getElementById("fingerApplet");
		if (fingerApplet) {
			fingerApplet.style.display = 'none';
			fingerApplet.style.display = '';
		}

		if (fingerPrintObj) fingerPrintObj.notifyOnImage(function (fingerObj: FingerPrintObjInstance): void { field.setImage(fingerObj); });
		if (fingerPrintObj) fingerPrintObj.notifyOnTemplate(function (fingerObj: FingerPrintObjInstance): void { field.setTemplate(fingerObj); });
	},
	sendImageToField: function (fieldName: string, formName?: string | null): void {
		if (formName == null || formName == '') formName = getElementObj(fieldName).formObj.name;
		fingerPrintObj!.notifyOnImage(function (fingerObj: FingerPrintObjInstance): void {
			getFormObj(formName!).getElement(fieldName).set(fingerObj.imageData);
		});
	},
	sendTemplateToField: function (fieldName: string, formName?: string | null): void {
		if (formName == null || formName == '') formName = getElementObj(fieldName).formObj.name;
		fingerPrintObj!.notifyOnTemplate(function (fingerObj: FingerPrintObjInstance): void {
			getFormObj(formName!).getElement(fieldName).set(fingerObj.templateData);
		});
	},
	image64: function (imageData: string): void {
		if (fingerPrintObj) fingerPrintObj.setImageData(imageData);
	},
	template64: function (templateData: string): void {
		if (fingerPrintObj) fingerPrintObj.setTemplateData(templateData);
	},
	writeApplet: function (displayMode?: string | null, version?: number, width?: number, height?: number): void {
		if (fingerPrintObj != null) return;
		if (/Mac OS/.test(navigator.userAgent)) return;

		version = version || 2007;
		width = width || 95;
		height = height || 116;
		var params: AppletParams = {
			jnlp_href: "/jnlp/fingerprint_jnlp.jsp?__w=" + width + "&__h=" + height + "&__v=" + version
		};
		if (displayMode != null && displayMode != "") {
			params.jnlp_href += "&__displayMode=" + displayMode;
		}
		deployJava.runApplet(
			{
				id: 'fingerApplet',
				codebase: '/jars/finger' + version + '/',
				code: 'myassn.finger' + version + '.FingerApplet',
				width: width,
				height: height,
				MAYSCRIPT: true
			},
			params,
			'1.6'
		);
		fingerPrintObj = {
			applet: document.getElementById("fingerApplet") as unknown as FingerPrintApplet,
			imageData: "",
			templateData: "",
			width: width,
			height: height,
			quality: null,
			notifyOnImageFn: function (): void { },
			notifyOnTemplateFn: function (): void { },
			notifyOnImage: function (functionRef: (obj: FingerPrintObjInstance) => void): void {
				this.setImageEventEnabled(true);
				this.notifyOnImageFn = functionRef;
			},
			notifyOnTemplate: function (functionRef: (obj: FingerPrintObjInstance) => void): void {
				this.setTemplateEventEnabled(true);
				this.notifyOnTemplateFn = functionRef;
			},
			setImageData: function (newVal: string): void {
				this.imageData = newVal;
				this.notifyOnImageFn(this);
			},
			setTemplateData: function (newVal: string): void {
				this.templateData = newVal;
				this.quality = this.applet!.getTemplateQuality();
				this.notifyOnTemplateFn(this);
			},
			setImageEventEnabled: function (newVal?: boolean): void {
				try {
					this.applet!.setImageEventEnabled(newVal!);
				} catch (ex) {
					window.setTimeout(function (): void { fingerPrintObj!.setImageEventEnabled(); }, 500);
				}
			},
			setTemplateEventEnabled: function (newVal?: boolean): void {
				try {
					this.applet!.setTemplateEventEnabled(newVal!);
				} catch (ex) {
					window.setTimeout(function (): void { fingerPrintObj!.setTemplateEventEnabled(); }, 500);
				}
			},
			setImageType: function (newVal: string): void {
				this.applet!.setImageTypeAsString(newVal);
			},
			setImageQuality: function (newVal: number): void {
				this.applet!.setImageQuality(newVal);
			},
			setDisplayMode: function (newVal: string): void {
				this.applet!.setDisplayModeAsString(newVal);
			}
		};
	}
};

// Export to globalThis for backwards compatibility
(globalThis as any).fingerPrintObj = fingerPrintObj;
(globalThis as any).fingerPrint = fingerPrint;

if (_SR_ != null) _SR_.notify("fingerprint.js");

// ES module exports
export { fingerPrintObj, fingerPrint };
export type { FingerPrintModule, FingerPrintObjInstance, FingerPrintFieldInstance, FingerObject };
